import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { supabase, isAuthEnabled } from '../lib/supabase';
import { useUIStore } from '../stores/uiStore';
import { usePlayerStore } from '../stores/playerStore';
import { identify, reset as posthogReset } from '../lib/posthog';

const AuthContext = createContext(null);

const GUEST_USER = {
  id: 'guest-mode',
  email: 'guest@melodiza.local',
  isGuest: true,
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // BYPASS auth pour démos / fallback.
  // Garde-fou prod : on throw uniquement sur un hostname de prod réel
  // (pas localhost ni IP locale ni *.pages.dev preview ni app native Capacitor)
  // pour ne pas désactiver l'auth silencieusement en cas de mauvaise config réelle.
  const explicitBypass = import.meta.env.VITE_BYPASS_AUTH === 'true';
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const protocol =
    typeof window !== 'undefined' ? window.location.protocol : '';
  const isCapacitorNative =
    typeof window !== 'undefined' &&
    (!!window.Capacitor?.isNativePlatform?.() ||
      protocol === 'capacitor:' ||
      protocol === 'ionic:');
  const isLocalHost =
    !hostname ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.local') ||
    hostname.endsWith('.pages.dev');
  const isProductionDomain =
    import.meta.env.PROD && !isLocalHost && !isCapacitorNative;
  // Inside Capacitor (iOS/Android native) without Supabase env vars at build time,
  // silently fall back to guest mode (the !isAuthEnabled branch handles this).
  const bypassAuth = explicitBypass || !isAuthEnabled();

  if (isProductionDomain && !isAuthEnabled() && !explicitBypass) {
    throw new Error(
      '[AuthContext] Supabase config missing in production. ' +
        'Set VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY, ' +
        'or set VITE_BYPASS_AUTH=true to bypass intentionally.'
    );
  }

  useEffect(() => {
    if (bypassAuth) {
      setUser(GUEST_USER);
      setSession({ user: GUEST_USER, access_token: 'guest-token' });
      setLoading(false);
      return;
    }

    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (event === 'SIGNED_IN' && newSession?.user) {
        identify(newSession.user.id, { email: newSession.user.email });
      }
      if (event === 'SIGNED_OUT') {
        posthogReset();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [bypassAuth]);

  const sendMagicLink = async (email) => {
    if (bypassAuth) return { error: null };
    return supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin + '/auth/callback' },
    });
  };

  const signOut = async () => {
    if (bypassAuth) return;
    // multi-tenant safety: clear Zustand store state + localStorage before logout (R1-2)
    useUIStore.getState().resetUIStore();
    usePlayerStore.getState().resetPlayerStore();
    localStorage.removeItem('melodiza:ui');
    localStorage.removeItem('melodiza:player');
    posthogReset();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      isAuthenticated: !!user,
      isGuest: user?.isGuest === true,
      bypassAuth,
      sendMagicLink,
      signOut,
    }),
    // eslint-disable-next-line -- react-hooks/exhaustive-deps: intentional partial deps
    [user, session, loading, bypassAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
